import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Row, Col, Card } from "react-bootstrap";
import { toast } from "sonner";
import {
  addNewBlog,
  fetchBlogsList,
  updateExistingBlog,
  uploadBlogImage,
  fetchBlogCategoriesList,
  fetchBlogDetails,
  uploadBlogInlineImage,
  verifySlug,
} from "../../services/axios/apiManager";
import { Formik } from "formik";
import { blogValidationSchema } from "./blogValidations";
import { useNavigate, useParams } from "react-router-dom";
import { baseURL } from "../../services/axios/eps";
import { handleErrors } from "../../services/dataManager";
import LoadingSpinner from "../../Components/spinner";
import DashboardLayout from "../DashboardLayout";
import $ from "jquery";
import "summernote/dist/summernote-lite.css";
import "summernote/dist/summernote-lite.js";
import { useAppContext } from "../../services/context";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { blogTags } from "../../services/staticData";
import { customStyles } from "../games/customStyles";
import { SwitchButtonMedium } from "../../Components/switchButton";
import AddBlogCategoryModal from "./addBlogCategoryModal";

const AddEditBlog = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { user } = state;
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [blogData, setBlogData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showCatModal, setShowCatModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const animatedComponents = makeAnimated();
  const editorRef = useRef(null);
  const isSummernoteInited = useRef(false);
  const formikRef = useRef(null);

  const tagOptions = blogTags.map((tag) => ({
    value: tag,
    label: tag.replace(/-/g, " "),
  }));

  const fetchData = async () => {
    setInitialLoading(true);
    try {
      const [catRes] = await Promise.all([fetchBlogCategoriesList()]);

      if (catRes?.data?.success) setCategories(catRes.data.data);

      if (id) {
        const blogRes = await fetchBlogDetails(id);
        if (blogRes?.data?.success) {
          setBlogData(blogRes.data.data);
        } else {
          toast.error("Failed to fetch blog details");
          navigate("/BlogsManagement");
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setInitialLoading(false);
    }
  };

  const verifySlugIsUnique = async (slug) => {
    const res = await verifySlug({ slug });
    if (res?.data?.success) {
      return res.data.data;
    }
    return false;
  };
  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!initialLoading && editorRef.current && !isSummernoteInited.current) {
      $(editorRef.current).summernote({
        placeholder: "Enter blog content...",
        tabsize: 2,
        height: 400,
        toolbar: [
          ["style", ["style"]],
          ["font", ["bold", "underline", "clear"]],
          ["color", ["color"]],
          ["para", ["ul", "ol", "listStyles", "paragraph"]],
          ["table", ["table"]],
          ["insert", ["link", "picture", "video"]],
          ["view", ["fullscreen", "codeview", "help"]],
        ],
        callbacks: {
          onChange: function (contents) {
            if (formikRef.current) {
              formikRef.current.setFieldValue("content", contents);
            }
          },
          onImageUpload: function (files) {
            for (let i = 0; i < files.length; i++) {
              const file = files[i];
              const formData = new FormData();
              formData.append("image", file);
              uploadBlogInlineImage(formData)
                .then((res) => {
                  if (res?.data?.success) {
                    const url = `${baseURL}/uploads/file/${res.data.url}`;
                    $(editorRef.current).summernote("insertImage", url);
                  } else {
                    toast.error("Failed to upload inline image");
                  }
                })
                .catch((err) => {
                  console.error("Error uploading image:", err);
                  toast.error("Error uploading image");
                });
            }
          },
        },
      });
      isSummernoteInited.current = true;

      $(".note-editable").css({
        "background-color": "transparent",
        color: "#fff",
        "min-height": "400px",
      });

      if ($("#summernote-custom-styles").length === 0) {
        $("<style id='summernote-custom-styles'>")
          .prop("type", "text/css")
          .html(
            `
            .note-editable ul {
              list-style-type: disc !important;
              padding-left: 40px !important;
            }
            .note-editable ol {
              list-style-type: decimal !important;
              padding-left: 40px !important;
            }
            .note-editable li {
              display: list-item !important;
            }
          `,
          )
          .appendTo("head");
      }

      $(".note-toolbar").addClass("summernote-toolbar");

      if (blogData?.content) {
        $(editorRef.current).summernote("code", blogData.content);
      }
    }

    return () => {
      if (isSummernoteInited.current) {
        $(editorRef.current).summernote("destroy");
        isSummernoteInited.current = false;
      }
    };
  }, [initialLoading, blogData]);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleAddEditBlog = async (data) => {
    setLoading(true);
    try {
      // Only verify slug uniqueness if adding new or if slug changed
      if (!id || data.slug !== blogData?.slug) {
        const isSlugTaken = await verifySlugIsUnique(data.slug);
        if (isSlugTaken) {
          toast.error("Blog slug must be unique", { duration: 2000 });
          if (formikRef.current) {
            formikRef.current.setFieldError("slug", "Slug must be unique");
          }
          return;
        }
      }

      let blogId = id;
      let success = false;
      let newBlogId = null;
      if (blogId) {
        const editResponse = await updateExistingBlog({ id: blogId, ...data });
        if (editResponse?.data?.success) {
          success = true;
          toast.success("Blog has been updated", { duration: 2000 });
        } else {
          handleErrors(editResponse, navigate, "Update Blog:");
        }
      } else {
        const addResponse = await addNewBlog(data);
        if (addResponse?.data?.success) {
          success = true;
          newBlogId = addResponse.data.data.id;
          toast.success("New blog has been added", { duration: 2000 });
        } else {
          handleErrors(addResponse, navigate, "Add Blog:");
        }
      }

      if (success && selectedFile) {
        const formData = new FormData();
        formData.append("image", selectedFile);
        const uploadRes = await uploadBlogImage(blogId || newBlogId, formData);
        if (!uploadRes?.data?.success) {
          toast.error("Image upload failed");
        }
      }

      if (success) {
        navigate("/BlogsManagement");
      }
    } catch (error) {
      console.error("Error in add/edit blog:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center vh-100">
          <LoadingSpinner size="lg" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-4">
        <Card className="bg-dark text-light border-secondary">
          <Card.Header className="border-secondary d-flex justify-content-between align-items-center">
            <h3 className="text-success mb-0">
              {id ? "Edit Blog" : "Add New Blog"}
            </h3>
            <div>
              <Button
                variant="success"
                className="me-2"
                onClick={() => {
                  setEditData(null);
                  setShowCatModal(true);
                }}
              >
                Add New Category
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate("/BlogsManagement")}
              >
                Back to List
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {blogData?.image && (
              <div style={{ height: "500px", margin: "40px 0px 40px" }}>
                <img
                  crossorigin="anonymous"
                  src={
                    `${baseURL}/uploads/file/${blogData?.image}`
                    // imageUrl ||
                    // (orgsData?.image
                    //   ? orgsData.image.includes("https")
                    //     ? orgsData.image
                    //     : `${baseURL}/uploads/file/${orgsData.image}`
                    //   : "https://img.freepik.com/premium-vector/abstract-technology-background-with-molecular-structure-genetic-engineering-molecules-dna_120542-1393.jpg?semt=ais_hybrid")
                  }
                  alt=""
                  className="card-img-top profile-wid-img object-fit-cover"
                  style={{ maxHeight: 500 }}
                />
              </div>
            )}
            <Formik
              innerRef={formikRef}
              initialValues={{
                title: blogData?.title || "",
                slug: blogData?.slug || "",
                summary: blogData?.summary || "",
                content: blogData?.content || "",
                author_id: blogData?.author_id || user?.id,
                author_type: blogData?.author_type || "employee",
                category_id: blogData?.category_id || "",
                tags: blogData?.tags || "",
                commentable: blogData ? blogData?.commentable : 1,
                meta_title: blogData?.meta_title || "",
                meta_description: blogData?.meta_description || "",
                meta_keywords: blogData?.meta_keywords || "",
              }}
              enableReinitialize={true}
              validationSchema={blogValidationSchema}
              onSubmit={handleAddEditBlog}
            >
              {({
                handleChange,
                handleSubmit,
                values,
                errors,
                touched,
                setFieldValue,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="categorySelect">
                        <Form.Label>Category</Form.Label>
                        <Form.Select
                          style={{ height: "40px" }}
                          className="bg-dark text-light border-secondary"
                          value={values.category_id}
                          onChange={handleChange("category_id")}
                        >
                          <option value="">Select Category</option>
                          {categories?.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.title}
                            </option>
                          ))}
                        </Form.Select>
                        {errors.category_id && touched.category_id && (
                          <span className="text-danger">
                            {errors.category_id}
                          </span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tags (max 3 tags)</Form.Label>
                        <Select
                          closeMenuOnSelect={false}
                          components={animatedComponents}
                          isMulti
                          options={tagOptions}
                          styles={customStyles}
                          value={tagOptions.filter((option) =>
                            values.tags?.split(",").includes(option.value),
                          )}
                          onChange={(selectedOptions) => {
                            if (selectedOptions && selectedOptions.length > 3) {
                              toast.warning("You can only select up to 3 tags");
                              return;
                            }
                            setFieldValue(
                              "tags",
                              selectedOptions
                                ? selectedOptions
                                    .map((opt) => opt.value)
                                    .join(",")
                                : "",
                            );
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="blogTitle">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          type="text"
                          className="bg-dark text-light border-secondary"
                          value={values.title}
                          onChange={(e) => {
                            handleChange("title")(e);
                            // if (!id) {
                            const slug = e.target.value
                              .toLowerCase()
                              .trim()
                              .replace(/ /g, "-")
                              .replace(/[^\w-]+/g, "");
                            setFieldValue("slug", slug);
                            // }
                          }}
                        />
                        {errors.title && touched.title && (
                          <span className="text-danger">{errors.title}</span>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="blogSlug">
                        <Form.Label>Slug</Form.Label>
                        <Form.Control
                          type="text"
                          className="bg-dark text-light border-secondary"
                          value={values.slug}
                          onChange={handleChange("slug")}
                        />
                        {errors.slug && touched.slug && (
                          <span className="text-danger">{errors.slug}</span>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="blogImage">
                    <Form.Label>
                      {id ? "Change Featured Image" : "Featured Image"}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      className="bg-dark text-light border-secondary"
                      onChange={handleFileChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="blogSummary">
                    <Form.Label>Summary</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      className="bg-dark text-light border-secondary"
                      value={values.summary}
                      onChange={handleChange("summary")}
                    />
                    {errors.summary && touched.summary && (
                      <span className="text-danger">{errors.summary}</span>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Content</Form.Label>
                    <div
                      ref={editorRef}
                      className="bg-light text-dark border-secondary rounded"
                    />
                    {errors.content && touched.content && (
                      <span className="text-danger">{errors.content}</span>
                    )}
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="metaTitle">
                        <Form.Label>Meta Title</Form.Label>
                        <Form.Control
                          type="text"
                          className="bg-dark text-light border-secondary"
                          value={values.meta_title}
                          onChange={handleChange("meta_title")}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="metaDescription">
                        <Form.Label>Meta Description</Form.Label>
                        <Form.Control
                          type="text"
                          className="bg-dark text-light border-secondary"
                          value={values.meta_description}
                          onChange={handleChange("meta_description")}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3" controlId="metaTitle">
                        <Form.Label>Meta Keywords (Comma separated)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="meta_keywords"
                          value={values.meta_keywords}
                          onChange={handleChange}
                          className="bg-dark text-light border-secondary"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <div className="container-fluid my-2">
                        <div className="d-flex">
                          <SwitchButtonMedium
                            isOn={values.commentable === 1}
                            onClick={() =>
                              setFieldValue(
                                "commentable",
                                values.commentable === 1 ? 0 : 1,
                              )
                            }
                            color={"#00af43ff"}
                          />
                          <span className="mx-2">Allow Comments</span>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end mt-4">
                    {loading && <LoadingSpinner size="md" className="me-2" />}
                    <Button
                      variant="secondary"
                      className="me-2"
                      onClick={() => navigate("/BlogsManagement")}
                    >
                      Cancel
                    </Button>
                    <Button variant="success" type="submit" disabled={loading}>
                      {id ? "Save Changes" : "Create Blog"}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
        <AddBlogCategoryModal
          show={showCatModal}
          editData={editData}
          onHide={() => {
            setShowCatModal(false);
            setEditData(null);
          }}
          onSuccess={() => {
            fetchData();
          }}
        />
      </div>
    </DashboardLayout>
  );
};

export default AddEditBlog;
